import type { MediaKind } from '../api/types'

/** De langste zijde van de webversie. Uit `CLAUDE.md`, niet onderhandelbaar. */
const maxEdge = 1600

const webQuality = 0.85

/** Het miniatuur in de wachtrij en de galerij; een paar kilobyte. */
const posterEdge = 160

const posterQuality = 0.6

export interface Prepared {
  /** De naar 1600px geschaalde JPEG die de galerij toont. */
  web: Blob

  /** Klein miniatuur als data-URL, zodat de wachtrij een tab-herstart overleeft. */
  poster: string

  width: number
  height: number
  durationSeconds: number | null
}

export function kindOf(file: File): MediaKind {
  return file.type.startsWith('video/') ? 'Video' : 'Photo'
}

/**
 * Maakt van een gekozen bestand de webversie.
 *
 * Bij een foto lost dit meteen het HEIC-probleem op: iOS decodeert HEIC native
 * naar een canvas, dus wat er uitkomt is altijd een JPEG die elke browser kan
 * tonen — ook al is het origineel dat niet.
 *
 * Bij video wordt het frame op t=1s het posterbeeld. De echte video gaat als
 * origineel mee en wordt in het detailscherm afgespeeld.
 */
export async function prepare(file: File): Promise<Prepared> {
  return kindOf(file) === 'Video' ? prepareVideo(file) : preparePhoto(file)
}

async function preparePhoto(file: File): Promise<Prepared> {
  const source = await decode(file)

  try {
    const web = await render(source, source.width, source.height, maxEdge, webQuality)
    const poster = await renderDataUrl(source, source.width, source.height)

    return {
      web,
      poster,
      width: web.width,
      height: web.height,
      durationSeconds: null,
    }
  } finally {
    // Een ImageBitmap houdt zijn pixels vast tot je hem sluit; een <img> heeft
    // geen close en wordt gewoon opgeruimd.
    source.image.close?.()
  }
}

async function prepareVideo(file: File): Promise<Prepared> {
  const url = URL.createObjectURL(file)
  const video = document.createElement('video')

  video.preload = 'metadata'
  video.muted = true
  video.playsInline = true
  video.src = url

  try {
    await once(video, 'loadedmetadata')

    // Het eerste frame van een telefoonvideo is vaak nog zwart of onscherp;
    // op één seconde staat er beeld. Is de video korter, dan het midden.
    const target = video.duration > 1.2 ? 1 : video.duration / 2

    video.currentTime = Number.isFinite(target) ? target : 0

    await once(video, 'seeked')

    const source = {
      image: video,
      width: video.videoWidth,
      height: video.videoHeight,
    }

    const web = await render(source, source.width, source.height, maxEdge, webQuality)
    const poster = await renderDataUrl(source, source.width, source.height)

    return {
      web,
      poster,
      width: web.width,
      height: web.height,
      durationSeconds: Number.isFinite(video.duration) ? video.duration : null,
    }
  } finally {
    video.removeAttribute('src')
    video.load()
    URL.revokeObjectURL(url)
  }
}

interface Source {
  image: CanvasImageSource & { close?: () => void }
  width: number
  height: number
}

/**
 * `createImageBitmap` met `imageOrientation: 'from-image'` past de
 * EXIF-oriëntatie zelf toe. Zonder dat staat elke staande telefoonfoto op zijn
 * kant in de galerij, want het canvas kijkt niet naar EXIF.
 */
async function decode(file: File): Promise<Source> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

      return { image: bitmap, width: bitmap.width, height: bitmap.height }
    } catch {
      // Valt hieronder terug: oudere Safari kent de optie niet.
    }
  }

  const url = URL.createObjectURL(file)
  const image = new Image()

  image.src = url

  try {
    await once(image, 'load')

    return { image, width: image.naturalWidth, height: image.naturalHeight }
  } finally {
    URL.revokeObjectURL(url)
  }
}

interface RenderedBlob extends Blob {
  width: number
  height: number
}

async function render(
  source: Source,
  width: number,
  height: number,
  edge: number,
  quality: number,
): Promise<RenderedBlob> {
  const scale = Math.min(1, edge / Math.max(width, height))

  const target = {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }

  const canvas = document.createElement('canvas')
  canvas.width = target.width
  canvas.height = target.height

  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Deze browser kan geen canvas tekenen.')
  }

  context.drawImage(source.image, 0, 0, target.width, target.height)

  const blob = await toBlob(canvas, 'image/jpeg', quality)

  return Object.assign(blob, target) as RenderedBlob
}

async function renderDataUrl(source: Source, width: number, height: number): Promise<string> {
  const scale = Math.min(1, posterEdge / Math.max(width, height))

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))

  canvas.getContext('2d')?.drawImage(source.image, 0, 0, canvas.width, canvas.height)

  return canvas.toDataURL('image/jpeg', posterQuality)
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Het beeld kon niet omgezet worden.'))),
      type,
      quality,
    )
  })
}

function once(target: HTMLElement, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    target.addEventListener(event, () => resolve(), { once: true })
    target.addEventListener('error', () => reject(new Error('Dit bestand kunnen we niet lezen.')), {
      once: true,
    })
  })
}
